import { useId, type HTMLAttributes, type ReactNode } from 'react';
import { Button, type ButtonSize } from './Button.js';
import { cx } from './cx.js';

/**
 * One answer, picked from a row of chips.
 *
 * A `Select` hides its options behind a menu, which is right when the list is
 * long or the answer is dull. This is the other case: three to thirteen answers
 * that are worth showing all at once, because comparing them *is* the choice —
 * four suits, thirteen ranks, four card stocks. The row wraps rather than
 * scrolls, so no answer is ever off screen.
 *
 * It was written out by hand three times inside `dev/showcase/deck.tsx`, which
 * is the copy the ladder calls the worst kind: the page that claims the design
 * system exists, contradicting it in its own class strings. What those three
 * copies each rebuilt is the part that gets forgotten — a `role="group"`, an
 * `aria-labelledby` pointing at a heading with a hand-written id, and
 * `aria-pressed` on **every** chip rather than only the chosen one. Drop the
 * last of those and a screen reader announces four ordinary buttons and never
 * says which one is on.
 *
 * **A chip is a `Button`, so this owns no look at all — the chosen one
 * included.** Every chip is the white `default`, and `aria-pressed` alone
 * presses the chosen one into the page: `ground-dark` under ink at the demi
 * weight, which is `Button`'s only ON state and now every toggle's (see
 * `PRESSED` in `Button.tsx`). This row is where that treatment was found, and
 * it took three tries, one per day, each killed by its own render: lemon
 * marked the only chip in the row that does nothing when pressed and the
 * loupe held three at once; gray-400 dressed the answer in the disabled stop;
 * gray-700 went muddy at chip dose, faded ink beside true black strokes. The
 * bench the candidates stood on was retired with the answer, on 2026-08-28,
 * and a lone toggle joined them on it the same day.
 *
 * **`aria-pressed`, not a radio group.** A radio group promises roving focus
 * and arrow-key movement between the options, and a `role` is a promise about
 * behavior — the `Cover` scar. These are toggle buttons in a named group: each
 * one is its own tab stop and says whether it is on. That is the pattern the
 * three hand copies already had, and it is honest about what the row does.
 */

export interface ChipOption {
  /** What this answer is, in code. Matches `value` when it is the chosen one. */
  readonly value: string;
  /** The word on the chip. Already translated. It is the chip's whole name. */
  readonly label: string;
  /**
   * A glyph, swatch, or pip drawn ahead of the word. Decorative by contract —
   * it is hidden from the accessible tree, so the label has to carry the
   * meaning on its own. It sits on the card-paper tile below; supply only the
   * mark itself.
   */
  readonly mark?: ReactNode;
}

export interface ChipGroupProps extends Omit<
  HTMLAttributes<HTMLDivElement>,
  'className' | 'style' | 'children' | 'onChange'
> {
  /** What the row is asking. Already translated; may emphasize part of the label. */
  readonly label: ReactNode;
  readonly value: string;
  readonly options: readonly ChipOption[];
  readonly onChange: (next: string) => void;
  /** Small chips retain the button’s 48px touch target. A chip has a word, so never an icon size. */
  readonly size?: Extract<ButtonSize, 'md' | 'sm'>;
  /** Layout only. The row and the chips are not yours. */
  readonly className?: string | undefined;
}

/* The name of the row is an overline, not a `FieldLabel`. A `FieldLabel` names
   a settings row, where the name and the control share a line at arm's length;
   this names a band of choices standing under it, which is the register
   `Section` uses for the same job. */
const NAME = 'm-0 text-xs font-extrabold uppercase tracking-widest text-muted';

const ROW = 'flex flex-wrap items-center gap-2';

/* Card paper behind every mark, in here rather than left to the caller, so a
   suit reads on a chip exactly as it reads on a card, whichever face the chip
   wears. On the white face it is near enough the white to barely show; on the
   chosen recess it is what keeps the mark on paper instead of on the page. */
const TILE =
  'inline-flex size-5 shrink-0 items-center justify-center rounded-sm bg-card-paper text-sm leading-none';

export function ChipGroup({
  label,
  value,
  options,
  onChange,
  className,
  size = 'md',
  ...rest
}: ChipGroupProps): React.JSX.Element {
  const nameId = useId();
  return (
    <div className={cx('flex flex-col gap-1', className)} {...rest}>
      <p className={NAME} id={nameId}>
        {label}
      </p>
      <div className={ROW} role="group" aria-labelledby={nameId}>
        {options.map((option) => (
          <Button
            key={option.value}
            size={size}
            aria-pressed={option.value === value}
            onClick={() => {
              onChange(option.value);
            }}
          >
            {option.mark !== undefined && (
              <span className={TILE} aria-hidden="true">
                {option.mark}
              </span>
            )}
            {option.label}
          </Button>
        ))}
      </div>
    </div>
  );
}
